import { RULES, type GameState, type Turn } from "./rules.js";

/**
 * Which celebration a turn earns, if any (PRD 6.4).
 *
 * Pure: it compares the state before and after a turn and returns at most one
 * result. The order below is the priority order from 6.4 and is load-bearing --
 * a 2,000 that also takes the lead should read as the 2,000, and anything that
 * reaches the target outranks everything.
 */

export type CelebrationKind =
  | "target"
  | "monster"
  | "big"
  | "thousand"
  | "lead"
  | "halfway"
  | "streak"
  | "penalty";

export interface Celebration {
  kind: CelebrationKind;
  /** Emoji, deliberately: it renders everywhere and needs no asset pipeline. */
  icon: string;
  /** German UI copy; DE is the source language. */
  title: string;
  detail: string;
  /** Penalties are bad news and must not look like a reward. */
  tone: "good" | "bad";
}

const HALFWAY = RULES.target / 2;

function leaderOf(state: GameState): string | null {
  const top = state.standings[0];
  // Nobody leads at 0-0: everyone would "take the lead" on the first turn.
  return top && top.total > 0 ? top.playerId : null;
}

/**
 * The ones worth interrupting for even when a group has asked for quiet.
 * Reaching the target, a monster throw, and the penalty -- everything else is
 * the noise the setting exists to remove.
 */
const RARE: ReadonlySet<CelebrationKind> = new Set<CelebrationKind>([
  "target",
  "monster",
  "penalty",
]);

export function isRare(kind: CelebrationKind): boolean {
  return RARE.has(kind);
}

export function celebrationFor(
  before: GameState,
  after: GameState,
  turn: Turn,
  name: string,
): Celebration | null {
  const was = before.players.find((p) => p.playerId === turn.playerId);
  const now = after.players.find((p) => p.playerId === turn.playerId);
  if (!now) return null;

  const previousTotal = was?.total ?? 0;

  // A strike that triggered the penalty. Checked before the good news, because
  // a penalty can coexist with nothing else worth announcing.
  if (turn.type === "strike" && (was?.penalties ?? 0) < now.penalties) {
    return {
      kind: "penalty",
      icon: "💥",
      title: `−${RULES.penalty.toLocaleString("de-DE")}`,
      detail: `${name} hat dreimal gestrichen`,
      tone: "bad",
    };
  }

  if (turn.type !== "bank") return null;

  if (now.total >= RULES.target && previousTotal < RULES.target) {
    return {
      kind: "target",
      icon: "🏁",
      title: "Im Ziel!",
      detail: `${name} hat ${RULES.target.toLocaleString("de-DE")} erreicht`,
      tone: "good",
    };
  }

  if (turn.points >= 2000) {
    // Deliberately does not name a combination: the app only ever sees a
    // number, and 2,000 can be thrown more ways than a Straße.
    return {
      kind: "monster",
      icon: "🚀",
      title: `${turn.points}!`,
      detail: `Riesenwurf, ${name}`,
      tone: "good",
    };
  }
  if (turn.points >= 1500) {
    return {
      kind: "big",
      icon: "🎯",
      title: `${turn.points}!`,
      detail: `Großer Wurf, ${name}`,
      tone: "good",
    };
  }
  if (turn.points >= 1000) {
    return {
      kind: "thousand",
      icon: "💪",
      title: turn.points.toLocaleString("de-DE"),
      detail: `Starker Wurf, ${name}`,
      tone: "good",
    };
  }

  // Taking the lead means overtaking somebody. The first scoring turn of a
  // game technically creates a leader, but announcing it is just noise.
  const leaderBefore = leaderOf(before);
  const leaderAfter = leaderOf(after);
  if (leaderBefore !== null && leaderAfter === turn.playerId && leaderBefore !== turn.playerId) {
    return { kind: "lead", icon: "👑", title: "In Führung!", detail: name, tone: "good" };
  }

  if (previousTotal < HALFWAY && now.total >= HALFWAY) {
    return {
      kind: "halfway",
      icon: "⚡",
      title: HALFWAY.toLocaleString("de-DE"),
      detail: `${name} ist über die Hälfte`,
      tone: "good",
    };
  }

  // Third consecutive banked turn, and every third one after that.
  if (now.streak >= 3 && now.streak % 3 === 0) {
    return {
      kind: "streak",
      icon: "🔥",
      title: `${now.streak}× in Folge`,
      detail: `${name} bleibt dran`,
      tone: "good",
    };
  }

  return null;
}
